import { PreRenderService } from './pre-render.service';
import { Injectable } from '@angular/core';
import { CacheService as AngularCache, CACHE, LifeSpan } from '@ngx-cache/core';
import { HttpParams } from '@angular/common/http';
import * as stringHash from 'string-hash';
import { TransferState, makeStateKey } from '@angular/platform-browser';
import { Filters } from '../classes/user-preferences';

const SERVER_TO_CLIENT_TTL = 30;

@Injectable()
export class CacheService {
  public KeySelectedListingId = 'google-map.selectedListingId';
  public KeySeenIntroAnimation = 'home-page.seenIntroAnimation';
  public KeyPreviouslySelectedListingIds = 'google-map.previouslySelectedListingIds';

  constructor(private readonly cache: AngularCache,
    private transferState: TransferState,
    private preRenderService: PreRenderService
  ) {
  }

  /**
   * Returns true if the cache contains an item with the provided key.
   * The catch is that even if an item is expired, it's key will still return true until you try and get it, at which
   * time it will check if it's still valid and then return nothing and then remove the item from the cache and key list.
   * @param hashKey
   */
  has(hashKey: string): boolean {
    return this.cache.has(hashKey);
  }

  /**
   * Removes the item if it exists.
   * @param hashKey hash key
   */
  remove(hashKey: string): void {
    return this.cache.remove(hashKey);
  }

  /**
   * It will return the object from the cache or undefined.
   * By default it assumes the object was stored as json and will attemp to json parse it before returning.
   * @param hashKey hash key
   * @param storedAsString if true, will not attempt to json parse before returning.
   */
  get<T>(hashKey: string, storedAsString?: boolean): T {
    let value = this.cache.get(hashKey);
    // The value either doesn't exist or has expired.
    if (!value && !this.preRenderService.isBrowserMode) {
      return undefined;
    } else if (!value && this.preRenderService.isBrowserMode) {
      // If in browser, check if value is in tranfer state

      value = this.transferState.get(makeStateKey(hashKey), null);
      if (value) {
        this.cache.set(hashKey, value, undefined, { TTL: SERVER_TO_CLIENT_TTL });
        this.transferState.remove(makeStateKey(hashKey));
      } else {
        return undefined;
      }
    } else if (this.preRenderService.isServerMode) {
      // If we manage to retrieve something from the cache in server mode, put it in tranfer state
      this.transferState.set(makeStateKey(hashKey), value);
    }

    if (!storedAsString) {
      return Object.assign(<T>{}, JSON.parse(value));
    }
    return value;
  }

  /**
   * WARNING: It is assumed the object is stored as json.
   * It will return the parsed object from the cache as an array of T or undefined.
   * @param hashKey hash key
   */
  getList<T>(hashKey: string): T[] {
    const json = this.cache.get(hashKey);
    // The value either doesn't exist or has expired.
    if (!json) {
      return undefined;
    }
    const jsonObject = JSON.parse(json);
    return jsonObject.map((r) => Object.assign(<T>{}, r));
  }

  /**
   * Either JSON.stringify the value or store it directly in cache for the secondsInCache.
   * @param hashKey hash key
   * @param value object to store
   * @param secondsInCache how long it should stay in cache
   * @param storeAsString if true, will not attempt to json parse before returning.
   */
  set(hashKey: string, value: any, secondsInCache: number, storeAsString?: boolean) {
    let stringToStore = value;
    if (!storeAsString) {
      stringToStore = JSON.stringify(value);
    }
    this.cache.set(hashKey, stringToStore, undefined, { TTL: secondsInCache });
  }

  /**
   * Gets a cache key from a url and params.
   * @param url The url
   * @param params The HttpParams object
   */
  getRequestHash(url: string, params?: HttpParams): string {
    if (!params) {
      params = new HttpParams();
    }
    return stringHash(url + params.toString());
  }

  /**
   * Gets a cache key from the passed in filter object.
   * @param url The url
   * @param params The HttpParams object
   */
  getFiltersHash(filters: Filters): string {
    if (!filters) {
      filters = new Filters();
    }
    return stringHash(JSON.stringify(filters));
  }
}
